<script setup lang="ts">
import type {MessageStatus} from "@/conversation/interfaces";
import {Check, CheckCheck, X} from "@lucide/vue";
import {computed} from "vue";
import Tooltip from "@/core/components/ui/tooltip/Tooltip.vue";
import TooltipTrigger from "@/core/components/ui/tooltip/TooltipTrigger.vue";
import TooltipContent from "@/core/components/ui/tooltip/TooltipContent.vue";
import TooltipProvider from "@/core/components/ui/tooltip/TooltipProvider.vue";

const props = defineProps<{
    status: MessageStatus | string;
    sentAt?: string | null;
    deliveredAt?: string | null;
    readAt?: string | null;
    failedAt?: string | null;
}>();

const icon = computed(() => {
    if (props.status === 'delivered' || props.status === 'read') return CheckCheck;
    if (props.status === 'failed' || props.status === 'rejected' || props.status === 'undeliverable') return X;
    return Check;
});

const colorClass = computed(() => {
    if (props.status === 'delivered' || props.status === 'read') return 'text-blue-400';
    if (props.status === 'failed' || props.status === 'rejected' || props.status === 'undeliverable') return 'text-red-400';
    return 'text-gray-400';
});

const formatDate = (date: string) =>
    new Date(date).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

const statusLines = computed(() => {
    const lines: { label: string; date: string }[] = [];

    if (props.sentAt) {
        lines.push({label: 'Envoyé', date: formatDate(props.sentAt)});
    }

    if (props.deliveredAt) {
        lines.push({label: 'Livré', date: formatDate(props.deliveredAt)});
    }

    if (props.readAt) {
        lines.push({label: 'Lu', date: formatDate(props.readAt)});
    }

    if (props.failedAt) {
        lines.push({label: 'Échec', date: formatDate(props.failedAt)});
    }

    return lines;
});
</script>

<template>
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger as-child>
        <component :is="icon" :class="['w-3.5 h-3.5 shrink-0 cursor-default', colorClass]" />
      </TooltipTrigger>
      <TooltipContent v-if="statusLines.length" side="top" :collision-padding="12" class="flex flex-col gap-1 text-xs">
        <div v-for="line in statusLines" :key="line.label" class="flex items-center gap-2">
          <span class="text-slate-300 font-medium w-12">{{ line.label }}</span>
          <span>{{ line.date }}</span>
        </div>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
</template>
